import { observer } from 'mobx-react-lite'
import React, { type UIEvent, useCallback, useMemo } from 'react'
import { Alert, Button, SpinnerLoader, TextInput } from 'shared/ui'
import { type IColumn, Table } from 'shared/ui/TableNew'
import { LINK_POWER_DIALER } from 'shared/constants/links'
import { getCellForNameDefault } from 'shared/helpers/getCellForNameDefault'
import { POWER_DIALER_NAME_LIMIT } from 'shared/constants/limits'
import { DeviceSettings } from 'entities/DeviceSettings'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { type RowContact } from 'entities/Contacts/model/Table/RowContact'
import { type PowerDialerEditorStore } from 'widgets/PowerDialer/ui/PowerDialerEditor/store/powerDialerEditorStore'
import { getCellNumber } from 'widgets/ContactEnrollment/ui/EnrollmentContactsTable'
import { SendFrom } from 'widgets/SendFrom'
import styles from './styles.module.scss'

type IPowerDialerEditorModalProps = {
  powerDialerStore: PowerDialerEditorStore
}

export const PowerDialerEditorModal = observer(
  ({ powerDialerStore }: IPowerDialerEditorModalProps) => {
    const { callStore } = useEntitiesContext()
    const { deviceSettingsStore, loading, testOutputDevice } = callStore
    const {
      editorActionButtonDisabled,
      handleCall,
      handleSaveDraft,
      showCloseConfirmation,
      deleteSelectedId,
      onChangeShowAlert,
      loadNextContactsPage,
      contactsStore: {
        tableStore,
        contacts,
        contactsRows,
        totalSelectedContacts,
        hasMoreContacts,
        isLoadingMore,
        isOnlyManualContactsSelection,
      },
      stateStore: {
        editorActionTooltip,
        showAlert,
        draftLoading,
        callLoading,
        name,
        nameError,
        setName,
        setNameError,
        isEditMode,
        sendFromStore,
      },
    } = powerDialerStore

    const rows = useMemo(
      () =>
        contactsRows.map((item) => {
          item.setAriaLabel('ContactItemRow')
          item.setActionsProps({
            noVisibleActions: false,
            actions: [
              {
                iconButtonProps: {
                  hidden: !isOnlyManualContactsSelection,
                  ariaLabel: 'PowerDialerClose',
                  icon: 'close',
                  variant: 'icon',
                  size: 'small',
                  color: 'tertiary',
                  onClick: () => {
                    deleteSelectedId(item.id)
                  },
                },
              },
            ],
          })

          return item
        }),
      [contacts]
    )
    const columns: IColumn<RowContact>[] = [
      {
        field: 'name',
        name: 'Name',
        width: '220px',
        maxWidth: '220px',
        renderRowCell: (row) => {
          return getCellForNameDefault({
            avatarInfo: row.origin.avatarInfo,
            fullName: row.origin.full_name,
          })
        },
      },
      {
        field: 'number',
        name: 'Phone',
        renderRowCell: getCellNumber,
      },
    ]

    const handleScroll = useCallback(
      (event: UIEvent) => {
        const target = event.target as HTMLDivElement
        const scrollHeight = target.scrollHeight
        const scrollTop = target.scrollTop
        const offsetHeight = target.offsetHeight

        if (isLoadingMore) return
        if (!hasMoreContacts) return
        if (scrollHeight <= scrollTop + offsetHeight + 200) {
          loadNextContactsPage()
        }
      },
      [isLoadingMore, hasMoreContacts]
    )

    const tableLoading = useMemo(() => {
      return isLoadingMore ? (
        <tr>
          <td colSpan={3}>
            <div className={styles.tableSpinner}>
              <SpinnerLoader ariaLabel={'PowerDialerEditorModal'} size={20} />
            </div>
          </td>
        </tr>
      ) : null
    }, [isLoadingMore])

    return (
      <div>
        <TextInput
          size={'large'}
          placeholder={'Enter campaign name'}
          value={name}
          setValue={setName}
          error={nameError}
          limit={POWER_DIALER_NAME_LIMIT}
          onFocus={() => setNameError('')}
          ariaLabel='PowerDialerName'
          className={styles.name}
          isAutoFocus
        />
        {showAlert && (
          <div className={styles.alert}>
            <Alert
              item={{
                type: 'infoBlue',
                desc: 'Have questions about this feature?',
              }}
              actions={[
                {
                  variant: 'link',
                  type: 'primary',
                  text: 'Learn more',
                  link: LINK_POWER_DIALER,
                },
              ]}
              onClose={() => {
                onChangeShowAlert(false)
              }}
            />
          </div>
        )}

        <div className={styles.section}>
          <SendFrom triggerVariant={'button'} store={sendFromStore} />
          <DeviceSettings
            deviceSettingsStore={deviceSettingsStore}
            dropdownProps={{
              placement: 'bottom-end',
              width: 246,
              margin: 4,
              disabled: loading,
              ariaLabel: 'deviceSettings',
            }}
            variantDropdownBtn={'icon'}
            testOutputDevice={testOutputDevice}
          />
        </div>

        <div className={styles.table}>
          <Table
            ariaLabel={'PowerDialerEditorModal'}
            store={tableStore}
            columns={columns}
            rows={rows}
            hidePagination
            isHoveredRowActions={isOnlyManualContactsSelection}
            isCursor={isOnlyManualContactsSelection}
            bottomTableContent={tableLoading}
            scrollbarProps={{
              className: styles.table__scroll,
              onScroll: handleScroll,
            }}
          />
        </div>

        <div className={styles.wrapBtn}>
          <Button
            text={'Cancel'}
            contained={'secondary'}
            style={{ marginRight: 'auto' }}
            onClick={showCloseConfirmation}
          />

          <Button
            contained={isEditMode ? 'primary' : 'secondary'}
            text={isEditMode ? 'Save changes' : 'Save as draft'}
            tooltipProps={{
              placement: 'top',
              label: editorActionTooltip,
            }}
            loading={draftLoading}
            disabled={editorActionButtonDisabled || callLoading}
            onClick={handleSaveDraft}
          />
          {!isEditMode && (
            <Button
              text={`Call ${totalSelectedContacts} contacts`}
              tooltipProps={{
                placement: 'top',
                label: editorActionTooltip,
              }}
              loading={callLoading}
              disabled={editorActionButtonDisabled || draftLoading}
              onClick={handleCall}
            />
          )}
        </div>
      </div>
    )
  }
)
